import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLiveRefresh } from '../../context/LiveUpdatesContext';
import { toast } from 'react-hot-toast';
import { Building, Check, X, MapPin, Phone, Mail } from 'lucide-react';
import { PageHeader, Button, StatusPill } from '../../components';
import { agencyService } from '../../services/agencyService';
import {
  CARD, FilterTabs, EmptyCard, ReasonModal, errorMessage, stampOf, CONNECTION_STATUS,
} from './shared';

/**
 * Agency > Care homes — who wants to book from this agency.
 *
 * A care home finds the agency by name and sends a request; nothing is shared
 * until the agency accepts it here. Accepting lets that home see the agency's
 * staff (to approve them for its own site) and send it cover requests.
 * Declined, cancelled and archived connections stay in History.
 */

const HomeCard = ({ c, children }) => {
  const meta = CONNECTION_STATUS[c.status] || { label: c.status, tone: 'mute' };
  return (
    <div className={`${CARD} p-5 flex flex-col gap-3`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[15px] font-bold text-[#0B1220] dark:text-gray-100 truncate">{c.home?.name}</p>
          {c.home?.group_name && <p className="text-[12px] text-[#94A3B8]">Part of {c.home.group_name}</p>}
        </div>
        <StatusPill tone={meta.tone}>{meta.label}</StatusPill>
      </div>
      <div className="flex flex-col gap-1 text-[12.5px] text-[#64748B] dark:text-gray-400">
        {c.home?.address && <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" />{c.home.address}</span>}
        {c.home?.phone && <span className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" />{c.home.phone}</span>}
        {c.home?.email && <span className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" />{c.home.email}</span>}
      </div>
      {c.message && c.status === 'pending' && (
        <p className="text-[13px] text-[#334155] dark:text-gray-300 bg-[#F5F8FC] dark:bg-gray-800/50 rounded-xl px-3 py-2">“{c.message}”</p>
      )}
      {c.reason && c.status !== 'connected' && c.status !== 'pending' && (
        <p className="text-[12.5px] text-[#64748B] dark:text-gray-400">Reason: {c.reason}</p>
      )}
      <p className="text-[12px] text-[#94A3B8]">
        {c.status === 'pending'
          ? `Requested ${stampOf(c.requested_at)}${c.requested_by ? ` by ${c.requested_by}` : ''}`
          : `Updated ${stampOf(c.updated_at)}`}
      </p>
      {children}
    </div>
  );
};

const AgencyHomes = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('pending');
  const [declining, setDeclining] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await agencyService.connections();
      setRows(res.data || []);
    } catch (err) {
      toast.error(errorMessage(err, 'Could not load care homes.'));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  // load() never shows a spinner after the first time, so it is already quiet.
  useLiveRefresh(['agency'], () => load());

  const groups = useMemo(() => ({
    pending: rows.filter((r) => r.status === 'pending'),
    connected: rows.filter((r) => r.status === 'connected'),
    history: rows.filter((r) => ['declined', 'cancelled', 'archived'].includes(r.status)),
  }), [rows]);

  // Land on Connected when nothing is waiting.
  useEffect(() => {
    if (!loading && tab === 'pending' && !groups.pending.length && groups.connected.length) setTab('connected');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const accept = async (c) => {
    setBusyId(c.id);
    try {
      const res = await agencyService.acceptConnection(c.id);
      toast.success(res.message);
      await load();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  const list = groups[tab] || [];

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Care homes"
        subtitle="Care homes that asked to book your staff. Accept a request to let that home review your staff and send you cover requests."
      />
      <FilterTabs
        value={tab}
        onChange={setTab}
        tabs={[
          { value: 'pending', label: 'Waiting for you', count: groups.pending.length, alert: groups.pending.length > 0 },
          { value: 'connected', label: 'Connected', count: groups.connected.length },
          { value: 'history', label: 'History', count: groups.history.length },
        ]}
      />
      {!loading && !list.length && (
        <EmptyCard
          icon={Building}
          title={tab === 'pending' ? 'No requests waiting' : tab === 'connected' ? 'No connected care homes yet' : 'No history yet'}
          text={tab === 'connected'
            ? 'Care homes find your agency by name and send a request. You can add your staff while you wait.'
            : undefined}
        />
      )}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
        {list.map((c) => (
          <HomeCard key={c.id} c={c}>
            {c.status === 'pending' && (
              <div className="flex gap-2 pt-1">
                <Button size="sm" leftIcon={<Check className="w-4 h-4" />} loading={busyId === c.id} onClick={() => accept(c)}>Accept</Button>
                <Button size="sm" variant="secondary" leftIcon={<X className="w-4 h-4" />} onClick={() => setDeclining(c)}>Decline</Button>
              </div>
            )}
          </HomeCard>
        ))}
      </div>

      <ReasonModal
        open={Boolean(declining)}
        title={`Decline ${declining?.home?.name || ''}`}
        intro="The care home is told you declined. They can send another request later."
        label="Reason (optional)"
        required={false}
        confirmLabel="Decline request"
        onClose={() => setDeclining(null)}
        onSubmit={async (reason) => {
          const res = await agencyService.declineConnection(declining.id, reason);
          toast.success(res.message);
          setDeclining(null);
          load();
        }}
      />
    </div>
  );
};

export default AgencyHomes;
